/**
 * Daily Metrics Page
 * Performance tracking with charts and trends
 */

import React, { useEffect, useState } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import '../styles/validation.css';

const API_BASE = 'https://trading-api-1075463475276.us-central1.run.app/api/trading-validation';

const DailyMetrics: React.FC = () => {
  const [trend, setTrend] = useState<any[]>([]);
  const [metrics, setMetrics] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [trendRes, metricsRes] = await Promise.all([
          fetch(`${API_BASE}/daily-trend`),
          fetch(`${API_BASE}/daily-metrics`),
        ]);

        const trendData = await trendRes.json();
        const metricsData = await metricsRes.json();

        setTrend(trendData.data || []);
        setMetrics(metricsData.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 300000); // 5 min
    return () => clearInterval(interval);
  }, []);

  if (loading) return <div className="validation-container"><div className="loading">Loading metrics...</div></div>;

  return (
    <div className="validation-container">
      <div className="validation-header">
        <h1>Daily Performance Metrics</h1>
        <p className="subtitle">{metrics?.date || 'Today'}'s trading performance</p>
      </div>

      {/* Key Metrics */}
      {metrics && (
        <div className="metrics-grid">
          <div className="metric-card">
            <div className="metric-label">Win Rate</div>
            <div className="metric-value">{(metrics.win_rate * 100).toFixed(0)}%</div>
            <div className="metric-breakdown">{metrics.wins}W / {metrics.losses}L</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Daily Return</div>
            <div className="metric-value">+{(metrics.return_pct * 100).toFixed(1)}%</div>
            <div className="metric-breakdown">{metrics.trades} trades</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Sharpe Ratio</div>
            <div className="metric-value">{metrics.sharpe.toFixed(2)}</div>
            <div className="metric-breakdown">Risk-adjusted returns</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Avg Win</div>
            <div className="metric-value">${metrics.avg_win.toFixed(0)}</div>
            <div className="metric-breakdown">vs ${Math.abs(metrics.avg_loss).toFixed(0)} loss</div>
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="charts-grid-full">
        {/* Return Trend */}
        <div className="widget chart-widget">
          <h3>Return Trend (40 Days)</h3>
          {trend.length > 0 && (
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="return_pct" stroke="#4CAF50" strokeWidth={2} name="Daily Return %" />
                <Line type="monotone" dataKey="cumulative_return" stroke="#2196F3" strokeWidth={2} name="Cumulative %" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Win Rate Trend */}
        <div className="widget chart-widget">
          <h3>Win Rate Trend</h3>
          {trend.length > 0 && (
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="win_rate" fill="#4CAF50" name="Win Rate" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Trade Breakdown */}
      {metrics && (
        <div className="widget breakdown-widget">
          <h3>Trade Details</h3>
          <div className="breakdown-grid">
            <div className="breakdown-item">
              <span className="label">Largest Win:</span>
              <span className="value highlight">${metrics.largest_win}</span>
            </div>
            <div className="breakdown-item">
              <span className="label">Largest Loss:</span>
              <span className="value">${metrics.largest_loss}</span>
            </div>
            <div className="breakdown-item">
              <span className="label">Profit Factor:</span>
              <span className="value">{metrics.profit_factor.toFixed(2)}</span>
            </div>
            <div className="breakdown-item">
              <span className="label">Drawdown:</span>
              <span className="value">{metrics.drawdown.toFixed(1)}%</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyMetrics;
